import { honoPrivateClient } from '@/api/honoClient';
import { useState, useTransition } from 'react';
import { Button } from '../../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/Dialog';
import { SbSubscription } from '@fd/supabase/types';
import { usePostHog } from 'posthog-js/react';
import useSbSubscription from '@/hooks/queries/useSbSubscription';
import PosthogSurvey from '../../PosthogSurvey';
import { Loader2 } from 'lucide-react';

export default function CancelSubscriptionButton(props: {
  subscription?: SbSubscription;
}) {
  const { subscription } = props;
  const { mutate, isValidating } = useSbSubscription();
  const [isCancelling, startTransition] = useTransition();
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);
  const posthog = usePostHog();

  const handleCancel = async () => {
    startTransition(async () => {
      await honoPrivateClient.subscription.cancel.$post();
      await mutate();
      posthog.capture('Subscription cancelled');
      setIsDialogueOpen(false);
    });
  };

  const handleOpenDialogue = async () => {
    await mutate();
    if (subscription?.active) {
      setIsDialogueOpen(true);
    }
  };

  const handleCloseDialogue = () => {
    setIsDialogueOpen(false);
  };

  if (!subscription) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpenDialogue}
        disabled={isCancelling || !subscription.active || isValidating}
      >
        {isValidating && <Loader2 className="animate-spin" />}
        {subscription.active ? 'Cancel subscription' : 'Subscription cancelled'}
      </Button>
      {isDialogueOpen && (
        <CancelSubscriptionDialogue
          isOpen={isDialogueOpen}
          onClose={handleCloseDialogue}
          onCancel={handleCancel}
          isLoading={isCancelling}
        />
      )}
    </>
  );
}

function CancelSubscriptionDialogue({
  isOpen,
  onClose,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const posthog = usePostHog();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to cancel your subscription?
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. Your remaining traffic will be
            available until the end of the current billing period.
          </DialogDescription>
        </DialogHeader>
        <PosthogSurvey
          rootElementId="cancel-subscription-dialogue"
          onSubmit={(id, answers) => {
            posthog.capture('survey sent', {
              $survey_id: id,
              ...answers,
            });
            onCancel();
          }}
          onCancel={onClose}
          isLoading={isLoading}
        />
      </DialogContent>
    </Dialog>
  );
}
